import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { toastMessage, Button, Input, Dropdown, Checkbox, Loader, AddRemove } from "../utils";
import { useNavigate } from "react-router-dom";
import { buttonClasses } from "../utils/theme";
import { useAddDeviceConfigMutation, useGetDefaultDeviceConfigMutation, useUpdateDeviceConfigMutation } from "../../api/deviceSlice";
import { useCheckpoints, useOrgs, usePlant, useUserDetail, useUserPermissions } from "../../services/hooks";
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from "@mui/material";
import { ExpandMoreIcon, InfoIcon } from "../../services/icons";
import { langToSpeakerMap, speechLanguageOptions } from "./speakerData";
import { capitalizeCamelCase } from "../../services/functions";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DragIndicatorIcon } from "../../services/icons";
import { usePlantContext } from "../../context/plantContext";
import { valid_email, valid_mobile } from "../../services/validation";
const DEFAULT_LOG_PATTERN = [{ commandCode: "", isLog: true, logTime: 0, keys: [{ key: "", value: "" }] }];
const DEFAULT_VALUE_CONFIG = [{ key: "", value: "" }];
const DEFAULT_ANTENNA = [{ antennanumber: "", status: "", action: "", checkpointId: "", readType: "" }];
const DEFAULT_INIT_CONFIGURATION_TYPE = [
	{ label: "Static", value: "static" },
	{ label: "Dynamic", value: "dynamic" },
	{ label: "AntennaPower", value: "AntennaPower" }
];

const defaultFormData = {
	dataSeparator: "",
	startSeparator: "",
	endSeparator: "",
	commandIndex: "",
	displayName: "",
	tidPattern: "",
	imei: "",
	power: "",
	orgId: "",
	plantId: "",
	deviceType: "",
	dataTarget: "VMS",
	deviceSubType: "",
	hardwareType: "",
	serialNo: "",
	characterEncoding: "utf-8",
	ledMsgType: "",
	ledStaticMsg: "",
	proximityMode: false,
	disableVahanVerification: false,
	speechLanguage: "",
	speechVoice: "",
	initConfig: [
		{
			type: "static",
			command: "",
			isActive: false,
			duration: "",
			order: 1,
			parameters: [""],
		},
	],
	alertConfig: {
		JbOpen: {
			alertThreshold: 0,
			cooldownPeriod: 0,
			notifyEmails: [],
			notifyMobiles: []
		},
		BoomOpen: {
			alertThreshold: 0,
			cooldownPeriod: 0,
			notifyEmails: [],
			notifyMobiles: []
		}
  	},
	offlineConfiguration:{
		gateInService:{
			boomTimeout:0,
			ledPrefix:"",
			ledSuffix:""
		},
		gateOutService:{
			boomTimeout:0,
			ledPrefix:"",
			ledSuffix:""
		},
	}

};

function updateLogPattern(logs) {
	return logs.map((_) => {
		const { commandCode = 0, logTime = 0, ...keys } = _;
		return {
			commandCode,
			logTime,
			isLog: logTime != undefined,
			keys: Object.keys(keys).map((key) => {
				return { key: key || "", value: keys[key] || "" };
			}),
		};
	});
}
function updateValueConfig(valueConfig = {}) {
	// console.log(valueConfig);
	return Object.keys(valueConfig).map((key) => ({ key, value: valueConfig[key] }));
}
function fillFormData(data) {
	return {
		dataSeparator: data.dataSeparator || "",
		startSeparator: data.startSeparator || "",
		endSeparator: data.endSeparator || "",
		commandIndex: data.commandIndex || 0,
		displayName: data.Displayname || data.displayName || "",
		tidPattern: data.tidPattern || "",
		imei: data.imei || "",
		power: data.power || "",
		orgId: data.orgId || "",
		plantId: data.plantId || "",
		deviceType: data.deviceType || "",
		dataTarget: data.dataTarget || "VMS",
		deviceSubType: data.deviceSubType || "",
		hardwareType: data.hardwareType || "",
		serialNo: data.serialNo || "",
		characterEncoding: data.characterEncoding || "",
		deduplicationTimeout: data.deduplicationTimeout || 0,
		outOfRangeTimeout: data.outOfRangeTimeout || 0,
		ledMsgType: data.ledMsgType || "",
		ledStaticMsg: data.ledStaticMsg || "",
		proximityMode: data.proximityMode || false,
		disableVahanVerification: data.disableVahanVerification || false,
		speechLanguage: data.speechLanguage || "",
		speechVoice: data.speechVoice || "",
		initConfig: data.initConfig
			? data.initConfig.map((item, index) => {
					if (item.type === "AntennaPower") {
						return {
							...item,
							antennaId: item.antennaSetting?.antennaId || "",
							power: item.antennaSetting?.power || "",
							order: item.order || index + 1,
						};
					}
					// Handle dynamic configs where command is the generator function string
					if (item.type === "dynamic") {
						return {
							...item,
							command: item.generatorFunction || "", // Ensure command is string
							parameters: item.parameters || [],
							order: item.order || index + 1,
						};
					}
					// Handle static configs
					return {
						...item,
						command:
							typeof item.command === "string"
								? data.deviceType === "led" || data.deviceType === "speaker"
									? { lineOne: item.lineOne || "", lineTwo: item.lineTwo || "" }
									: item.command
								: item.command || (data.deviceType === "led" || data.deviceType === "speaker" ? { lineOne: "", lineTwo: "" } : ""),
						parameters: item.parameters || [],
						order: item.order || index + 1,
					};
			  })
			: [
					{
						type: "static",
						command: data.deviceType === "led" || data.deviceType === "speaker" ? { lineOne: "", lineTwo: "" } : "",
						isActive: false,
						duration: "",
						parameters: [],
						order: 1,
					},
			  ],
		alertConfig : data?.alertConfig || defaultFormData.alertConfig,
		offlineConfiguration: data?.offlineConfiguration || defaultFormData.offlineConfiguration
	};
}

const AddDeviceForm = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { data: updateData, filterData: filterData } = location.state || {};
	const isUpdate = location.pathname.includes("edit");
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const { plantId } = usePlantContext();
	const [formData, setFormData] = useState(() => {
		const initialData = updateData ? fillFormData(updateData) : defaultFormData;

		if (!initialData.initConfig || initialData.initConfig.length === 0) {
			return {
				...initialData,
				initConfig: [
					{
						type: "static",
						command: initialData.deviceType === "led" || initialData.deviceType === "speaker" ? { lineOne: "", lineTwo: "" } : "",
						isActive: false,
						duration: "",
						parameters: [],
						order: 1,
					},
				],
			};
		}

		return initialData;
	});
	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions?.find((_) => _.value == formData.orgId) || null;
	const { plantOptions, clearPlants, getPlantDetails } = usePlant();
	const plantValue = plantOptions.find((_) => formData.plantId == _.value);
	const {checkpointOptions, getCheckpoints} = useCheckpoints(null, "anternnaCheckpoints")
	useEffect(() => {
		if (updateData) {
			getPlantDetails({ orgId: updateData?.orgId });
			getCheckpoints({plantIds: updateData.plantId })
		}
		else{
			getCheckpoints({plantIds: plantId||"" })
		}
	}, []);

	const [getDefaultDeviceConfig, { isLoading: isDefaultConfigFetching }] = useGetDefaultDeviceConfigMutation({});
	const handleFormChange = (e) => {
		let { name, value, checked, type } = e.target;
		value = type == "checkbox" ? checked : value;
		setFormData((old) => ({ ...old, [name]: value }));
		// console.log(formData)
		setError((prev) => {
			if (!prev[name]) return prev;
			const { [name]: _, ...rest } = prev;
			let fieldError;
			if (name === "imei") {
				if (!value) fieldError = "IMEI is required";
				else if (!value.match(/^[a-z0-9]+$/)) fieldError = "Special characters are not allowed, use only lowercase letters and digits";
			} else if (name === "displayName") {
				if (!value) fieldError = "Display Name is required";
			}
			if (fieldError) return { ...rest, [name]: fieldError };
			return rest;
		});
	};
	const onDropdownChange = (option, name) => {
		// console.log(option, name)
		let value = Array.isArray(option) ? option.map((_) => _.value) : option?.value || "";
		handleFormChange({ target: { name, value } });

		if (name == "deviceType") {
			switch (value) {
				case "led":
				case "speaker":
					setFormData((o) => ({ ...defaultFormData, displayName: o.displayName, imei: o.imei, orgId: o.orgId, deviceType: value, plantId: o.plantId }));
					setLogs(DEFAULT_LOG_PATTERN);
					setConfigs(DEFAULT_VALUE_CONFIG);
					setAntenna(DEFAULT_ANTENNA);
					break;
				default:
					setFormData((o) => ({ ...o, deviceSubType: "" }));
					break;
			}
		}
		if (name == "deviceSubType" && formData.deviceType != "led") {
			if (value !== "customReader") {
				getDefaultDeviceConfig({ deviceType: formData.deviceType, deviceSubType: value })
					.unwrap()
					.then((res) => {
						if (res.error) {
							toastMessage(false, res.message);
						} else {
							setFormData((o) => ({ ...fillFormData(res.data), displayName: o.displayName, imei: o.imei, orgId: o.orgId }));
							setLogs(updateLogPattern(res.data.logPattern));
							setAntenna(res.data.antenna);
							setConfigs(updateValueConfig(res.data.valueConfig));
						}
					});
			}
		}

		if (name == "orgId") {
			if (value) {
				getPlantDetails({ orgId: value });
			} else {
				clearPlants();
			}
		}
		if(name == "plantId"){
			getCheckpoints({plantIds: value})
		}
	};

	useEffect(() => {
		if (isUpdate && !updateData) {
			navigate("/devices");
		}
	}, []);
	const deviceTypeValue = deviceTypeOptions.find((_) => _.value == formData.deviceType);
	const dataTargetValue = dataTargetOption.find((_) => _.value == formData.dataTarget);
	const deviceSubTypeValue = (formData.deviceType == "led" ? ledSubTypeOptions : rfidSubTypeOptions).find((_) => _.value == formData.deviceSubType);
	const charEncodingValue = charEncodingOptions.find((_) => _.value == formData.characterEncoding);
	const ledMsgTypeValue = ledStatusOpts.find((_) => _.value == formData.ledMsgType);
	const speechLanguageValue = speechLanguageOptions.find((_) => _.value == formData.speechLanguage);

	const speechVoiceOptions = langToSpeakerMap[formData.speechLanguage] || [];
	const speechVoiceValue = speechVoiceOptions.find((_) => _.value == formData.speechVoice);

	let hardwareTypeOpts = [];
	let hardwareTypeValue = "";
	let isHardwareTypeDropdown = false;
	switch (formData.deviceType) {
		case "rfidReader":
			hardwareTypeOpts = hardwareTypeOptions;
			
			hardwareTypeValue = hardwareTypeOpts.find((_) => _.value == formData.hardwareType) || null;
			isHardwareTypeDropdown = true;
			break;
		case "camera":
			hardwareTypeOpts = cameraHardwareTypeOpt;
			hardwareTypeValue = hardwareTypeOpts.find((_) => _.value == formData.hardwareType) || null;
			isHardwareTypeDropdown = true;
			break;
		case "led":
		case "workstation":
		case "speaker":
			hardwareTypeValue = formData.hardwareType;
			isHardwareTypeDropdown = false;
			break;
	}
	// console.log({ hardwareTypeOpts, hardwareTypeValue, isHardwareTypeDropdown });

	const [logs, setLogs] = useState(updateData ? updateLogPattern(updateData.logPattern) : DEFAULT_LOG_PATTERN);
	const addLog = () => {
		setLogs((old) => [
			...old,
			{
				commandCode: "",
				isLog: true,
				logTime: 0,
				keys: [{ key: "", value: "" }],
			},
		]);
	};
	let gpoOptions;
	if (formData.deviceType === "rfidReader" && formData.deviceSubType) {
		const matchedItem = logs?.find((item) => /^(gpi?o)$/i.test(item.commandCode || ""))?.keys;
		gpoOptions = Array.isArray(matchedItem)
			? matchedItem
					.filter((item) => !["commandCode", "logTime"].includes(item.key))
					.map((item) => ({
						label: capitalizeCamelCase(item.key),
						value: item.key,
					}))
			: [];
	} else {
		gpoOptions = [];
	}

	const moveInitConfigItem = (dragIndex, hoverIndex) => {
		setFormData((old) => {
			const newInitConfig = [...old.initConfig];
			const draggedItem = newInitConfig[dragIndex];

			// Remove the dragged item
			newInitConfig.splice(dragIndex, 1);
			// Insert it at the hover position
			newInitConfig.splice(hoverIndex, 0, draggedItem);

			// Update order for all items
			return {
				...old,
				initConfig: newInitConfig.map((item, idx) => ({
					...item,
					order: idx + 1,
				})),
			};
		});
	};
	const removeLog = (index) => setLogs((old) => old.filter((_, i) => i != index));
	const handleLogChange = (e, index) => {
		let { name, value, checked, type } = e.target;
		value = type == "checkbox" ? checked : value;
		setLogs((old) => old.map((lp, i) => (i == index ? { ...lp, [name]: value } : lp)));
	};
	const handleKeysChange = (e, outerIndex, innerIndex) => {
		let { name, value } = e.target;
		setLogs((old) => old.map((lp, i) => (i == outerIndex ? { ...lp, keys: lp.keys.map((k, j) => (j == innerIndex ? { ...k, [name]: value } : k)) } : lp)));
	};
	const addKey = (outerIndex) => {
		setLogs((old) => old.map((lp, i) => (i == outerIndex ? { ...lp, keys: [...lp.keys, { key: "", value: "" }] } : lp)));
	};
	const removeKey = (outerIndex, innerIndex) => {
		setLogs((old) => old.map((lp, i) => (i == outerIndex ? { ...lp, keys: lp.keys.filter((_, j) => j != innerIndex) } : lp)));
	};

	const [configs, setConfigs] = useState(updateData ? updateValueConfig(updateData.valueConfig) : DEFAULT_VALUE_CONFIG);
	const handleConfigChange = (e, index) => {
		let { name, value } = e.target;
		setConfigs((old) => old.map((c, i) => (i == index ? { ...c, [name]: value } : c)));
	};
	const addConfig = () => setConfigs((old) => [...old, { key: "", value: "" }]);
	const removeConfig = (index) => setConfigs((old) => old.filter((_, i) => i != index));

	const [antenna, setAntenna] = useState(updateData ? updateData.antenna : DEFAULT_ANTENNA);
	const antennaIdOpts = antenna?.filter(a => a.antennanumber)?.map((_) => ({ label: _.antennanumber, value: _.antennanumber })) || [];
	const initConfigTypeOptions = formData.deviceType === "rfidReader" ? DEFAULT_INIT_CONFIGURATION_TYPE : DEFAULT_INIT_CONFIGURATION_TYPE.filter(opt => opt.value !== "AntennaPower");
	const handleAntennaChange = (e, index) => {
		let { name, value } = e.target;
		setAntenna((old) => old.map((c, i) => (i == index ? { ...c, [name]: value } : c)));
	};
	const addAntenna = () => setAntenna((old) => [...old, { antennanumber: "", status: "", action: "", checkpointId: "", readType: "" }]);
	const removeAntenna = (index) => setAntenna((old) => old.filter((_, i) => i != index));

	const [error, setError] = useState({});

	const [handleAddDevice, { isLoading: isAdding }] = useAddDeviceConfigMutation();
	const [handleUpdateDevice, { isLoading: isUpdating }] = useUpdateDeviceConfigMutation();

	const handleFormSubmit = async () => {
		const finalPlantId = userDetail?.userType === "superAdmin" ? formData.plantId : plantId;
		const plantName = plantOptions.find((plant) => plant.value === finalPlantId)?.label || "";
		// console.log("plantname", plantName)
		let payload = {
			...formData,
			orgName: orgValue?.label || userDetail.orgName,
			plantId: userDetail?.userType === "superAdmin" ? formData.plantId : plantId,
			plantName: plantName,
			offlineConfiguration: formData.offlineConfiguration,
			logPattern: logs.map((item) => ({
				commandCode: item.commandCode,
				logTime: item.logTime,
				...item.keys.reduce((res, { key, value }) => {
					res[key] = value;
					return res;
				}, {}),
			})),
			valueConfig: configs.reduce((res, { key, value }) => {
				res[key] = value;
				return res;
			}, {}),
			antenna,
		};
		if (formData.deviceType !== "workstation") {
			delete payload.offlineConfiguration;
		}
		payload.initConfig = formData.initConfig.map((config) => {
			if (config.type === "static") {
				const baseConfig = {
					type: config.type,
					isActive: config.isActive,
					duration: config.duration,
					order: config.order,
				};

				if (formData.deviceType === "led" || formData.deviceType === "speaker") {
					return {
						...baseConfig,
						command: {
							//for led and Speaker as object
							lineOne: config.command?.lineOne || "",
							lineTwo: config.command?.lineTwo || "",
						},
					};
				} else {
					return {
						...baseConfig,
						command: config.command, // Keep as string for RFID
					};
				}
			} else if (config.type === "dynamic") {
				return {
					type: config.type,
					generatorFunction: config.command,
					parameters: config.parameters || [],
					isActive: config.isActive,
					duration: config.duration,
					order: config.order,
				};
			} else if (config.type === "AntennaPower") {
				return {
					type: config.type,
					isActive: config.isActive,
					duration: config.duration,
					order: config.order,
					antennaSetting: {
						antennaId: config.antennaId || "",
						power: config.power !== "" && config.power !== undefined && config.power !== null 
							? Number(config.power) 
							: 0,
					},
				};
			}
			return config;
		});

		if (isUpdate) {
			payload.deviceId = updateData?._id;
			if (userDetail.userType != "superAdmin") {
				delete payload.orgId;
			}
		}
		// console.log("payload", payload);

		const {
			data: { error, message },
		} = isUpdate ? await handleUpdateDevice(payload) : await handleAddDevice(payload);

		// console.log({error, message})
		if (error) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			navigate("/devices", { state: { filterData: filterData || "" } });
		}
	};

	const handleFormValidation = () => {
		let error = {};

		if (userDetail?.userType == "superAdmin" && !formData.orgId) error.orgId = "Organization is required";
		if (!formData.plantId?.length && userDetail?.userType == "superAdmin") error.plantId = "Warehouse is required";

		// if (!formData.dataSeparator) error.dataSeparator = "Data Separator is required";
		// if (!formData.startSeparator) error.startSeparator = "Start Separator is required";
		// if (!formData.endSeparator) error.endSeparator = "End Separator is required";
		// if (!formData.commandIndex) error.commandIndex = "Command Index is required";

		if (!formData.displayName) error.displayName = "Display Name is required";
		// if (!formData.tidPattern) error.tidPattern = "TID Pattern is required";
		// if (!formData.imei) error.imei = "IMEI is required";
		if (!formData.imei) {
			error.imei = "IMEI is required";
		} else if (formData.deviceSubType!== "seuic" && formData.deviceSubType !== "zebra" && !formData.imei.match(/^[a-z0-9]+$/)) {
			error.imei = "IMEI must contain only lowercase letters and digits";
		}
		if (!formData.deviceType) error.deviceType = "Device Type is required";
		else if (formData.deviceType == "rfidReader" && !formData.deviceSubType) error.deviceSubType = "Device Subtype is required";
		else if (formData.deviceType == "led" && !formData.ledMsgType) error.ledMsgType = "LED Message Type is required";
		if (formData.deviceType == "rfidReader" && !formData.dataTarget) error.dataTarget = "Data Target is required";
		if(formData?.alertConfig?.JbOpen){
			let data = formData?.alertConfig?.JbOpen;
			if(data.alertThreshold || data.cooldownPeriod || data.notifyEmails.length > 0 || data.notifyMobiles.length > 0){
				if(!data.alertThreshold){
					error['JbOpenAlertThreshold'] = "Alert Threshold is required"
				}
				if(!data.cooldownPeriod){
					error['JbOpenCooldownPeriod'] = "Cooldown Period is required"
				}
				// if(data.notifyEmails.length === 0){
				// 	error['JbOpenNotifyEmails'] = "Atleast one notifyEmails is required"
				// }
				// if(data.notifyMobiles.length === 0){
				// 	error['JbOpenNotifyMobiles'] = "Atleast one notifyMobiles is required"
				// }
			}

		}
		if(formData?.alertConfig?.BoomOpen){
			let data = formData?.alertConfig?.BoomOpen;
			if(data.alertThreshold || data.cooldownPeriod || data.notifyEmails.length > 0 || data.notifyMobiles.length > 0){
				if(!data.alertThreshold){
					error['BoomOpenAlertThreshold'] = "Alert Threshold is required"
				}
				if(!data.cooldownPeriod){
					error['BoomOpenCooldownPeriod'] = "Cooldown Period is required"
				}
				// if(data.notifyEmails.length === 0){
				// 	error['BoomOpenNotifyEmails'] = "Atleast one notifyEmails is required"
				// }
				// if(data.notifyMobiles.length === 0){
				// 	error['BoomOpenNotifyMobiles'] = "Atleast one notifyMobiles is required"
				// }
			}

		}
		if (Object.keys(error).length) {
			setError(error);
		} else {
			handleFormSubmit();
		}
	};

	// const handleInitConfigChange = (e, index) => {
	// 	const { name, value, type, checked } = e.target;

	// 	if (name === "duration") {
	// 		setFormData((old) => ({
	// 			...old,
	// 			initConfig: old.initConfig.map((item, i) =>
	// 				i === index
	// 					? {
	// 							...item,
	// 							[name]: value * 1000,
	// 					  }
	// 					: item
	// 			),
	// 		}));
	// 	} else if (name === "lineOne" || name === "lineTwo") {
	// 		setFormData((old) => ({
	// 			...old,
	// 			initConfig: old.initConfig.map((item, i) =>
	// 				i === index
	// 					? {
	// 							...item,
	// 							command: {
	// 								...item.command,
	// 								[name]: value,
	// 							},
	// 					  }
	// 					: item
	// 			),
	// 		}));
	// 	} else {
	// 		setFormData((old) => ({
	// 			...old,
	// 			initConfig: old.initConfig.map((item, i) =>
	// 				i === index
	// 					? {
	// 							...item,
	// 							[name]: type === "checkbox" ? checked : value,
	// 					  }
	// 					: item
	// 			),
	// 		}));
	// 	}
	// };
	const handleAlertConfigChange = (value, name, category) => {
		// console.log(name, value);
		// alertConfig.JbOpen.notifyMobiles
		if(category === 'JbOpen'){
			setFormData((old) => ({ ...old, 
				alertConfig : {
					...old.alertConfig,
					JbOpen : {
						...old.alertConfig.JbOpen,
						[name]:  Array.isArray(value) ? value.map((_)=> _.value) : value
						// Array.isArray(value) ? value.map((_)=> _.value) : value
					}
			}
		}));
		}
		if(category === 'BoomOpen'){
			setFormData((old) => ({ ...old, 
				alertConfig : {
					...old.alertConfig,
					BoomOpen : {
						...old.alertConfig.BoomOpen,
						[name]:  Array.isArray(value) ? value.map((_)=> _.value) : value
						// Array.isArray(value) ? value.map((_)=> _.value) : value
					}
			}
		}));
		}
		// console.log(formData)
		
	}
	const handleOfflineConfigChange = (value, name, serviceType) => {

		const isIntegerValue = name === 'boomTimeout' ? Number(value) : value;
		
		setFormData((old) => ({ 
			...old, 
			offlineConfiguration: {
			...old.offlineConfiguration,
			[serviceType]: {
				...old.offlineConfiguration[serviceType],
				[name]: isIntegerValue
			}
			}
		}));
	};
	const handleInitConfigChange = (e, index) => {
		const { name, value, type, checked } = e.target;

		if (name === "duration") {
			setFormData((old) => ({
				...old,
				initConfig: old.initConfig.map((item, i) =>
					i === index
						? {
								...item,
								[name]: value * 1000,
						  }
						: item
				),
			}));
		} else if (name === "lineOne" || name === "lineTwo") {
			setFormData((old) => ({
				...old,
				initConfig: old.initConfig.map((item, i) =>
					i === index
						? {
								...item,
								command: {
									...item.command,
									[name]: value,
								},
						  }
						: item
				),
			}));
		} else if (name === "command" && formData.initConfig[index].type === "dynamic") {
			// Handle direct command input for dynamic types
			setFormData((old) => ({
				...old,
				initConfig: old.initConfig.map((item, i) =>
					i === index
						? {
								...item,
								[name]: value,
						  }
						: item
				),
			}));
		} else {
			setFormData((old) => ({
				...old,
				initConfig: old.initConfig.map((item, i) =>
					i === index
						? {
								...item,
								[name]: type === "checkbox" ? checked : value,
						  }
						: item
				),
			}));
		}
	};
	const handleInitConfigDropdownChange = (option, name, index) => {
		setFormData((old) => {
			const newInitConfig = [...old.initConfig];

			if (name === "type") {
				// When type changes, reset all type-specific fields
				const baseConfig = {
					type: option?.value || "",
					isActive: newInitConfig[index].isActive,
					duration: newInitConfig[index].duration,
					order: newInitConfig[index].order,
				};

				if (option?.value === "static") {
					newInitConfig[index] = {
						...baseConfig,
						command: old.deviceType === "led" || old.deviceType === "speaker" ? { lineOne: "", lineTwo: "" } : "",
						parameters: [],
					};
				} else if (option?.value === "dynamic") {
					newInitConfig[index] = {
						...baseConfig,
						command: "",
						parameters: [""],
					};
				} else if (option?.value === "AntennaPower") {
					newInitConfig[index] = {
						...baseConfig,
						antennaId: "",
						power: "",
					};
				} else {
					newInitConfig[index] = baseConfig;
				}
			} else if (name === "antennaId") {
				// Handle dropdown clearing - option could be null, undefined, or object
				newInitConfig[index] = {
					...newInitConfig[index],
					antennaId: option?.value ?? "",
				};
			} else if (name === "power") {
				// Handle input clearing - option is the raw string value
				newInitConfig[index] = {
					...newInitConfig[index],
					power: option === "" || option === null || option === undefined ? "" : option,
				};
			} else {
				newInitConfig[index] = {
					...newInitConfig[index],
					[name]: option?.value || "",
				};
			}

			return {
				...old,
				initConfig: newInitConfig,
			};
		});
	};
	const addInitConfig = () => {
		setFormData((old) => ({
			...old,
			initConfig: [
				...old.initConfig,
				{
					type: "static",
					command: old.deviceType === "led" || old.deviceType === "speaker" ? { lineOne: "", lineTwo: "" } : "",
					isActive: false,
					duration: "",
					parameters: [],
					order: old.initConfig.length + 1,
				},
			],
		}));
	};
	const removeInitConfig = (index) => {
		setFormData((old) => ({
			...old,
			initConfig: old.initConfig
				.filter((_, i) => i !== index)
				.map((item, idx) => ({
					...item,
					order: idx + 1,
				})),
		}));
	};

	const handleParameterChange = (e, configIndex, paramIndex) => {
		const { value } = e.target;
		setFormData((old) => ({
			...old,
			initConfig: old.initConfig.map((config, i) =>
				i === configIndex
					? {
							...config,
							parameters: config.parameters.map((param, j) => (j === paramIndex ? value : param)),
					  }
					: config
			),
		}));
	};

	const addParameter = (configIndex) => {
		setFormData((old) => ({
			...old,
			initConfig: old.initConfig.map((config, i) =>
				i === configIndex
					? {
							...config,
							parameters: [...config.parameters, ""],
					  }
					: config
			),
		}));
	};

	const removeParameter = (configIndex, paramIndex) => {
		setFormData((old) => ({
			...old,
			initConfig: old.initConfig.map((config, i) =>
				i === configIndex
					? {
							...config,
							parameters: config.parameters.filter((_, j) => j !== paramIndex),
					  }
					: config
			),
		}));
	};
	const showFields = formData.deviceType == "rfidReader" ? !!formData.deviceSubType : !!formData.deviceType;

	const showSeparators = formData.deviceType == "rfidReader" && formData.deviceSubType == "zebra";
	const showCIndex = showSeparators;
	const showPower = formData.deviceType == "rfidReader";
	const showTid = showPower;
	const showInfo = (showCIndex || showPower || showTid) && formData?.deviceSubType !== "customReader";
	const showLogs = showTid;
	const showVC = showTid;
	const showAnt = showTid;
	const showLedInfo = formData.deviceType == "led";
	const showSpeakerInfo = formData.deviceType == "speaker";
	const handleGoBack = () => {
		navigate("/devices", { state: { filterData: filterData || "" } });
	};
	return (
		<>
			<div className="contentpanel add_device_config">
				<div className="cust-row flex-algn-cent">
					<div className="cust-col-5">
						<h1 className="title">{isUpdate ? "Update Device" : "Add Device"}</h1>
					</div>
					<div className="cust-col-5 flex-jc-end">
						<div>
							<Button className="" text="Go Back" style={buttonClasses.lynkitOrangeEmpty} onClick={handleGoBack} />
						</div>
					</div>
				</div>
				<hr className="bgg" />

				<form>
					<div className="form-inputs">
						{userDetail?.userType == "superAdmin" ? (
							<>
								<Dropdown id="orgId" name="orgId" label="Organization Name" onChange={onDropdownChange} options={orgOptions} value={orgValue} error={error.orgId} />
								<Dropdown id="plantIdAdd" name="plantId" label="Warehouse" onChange={onDropdownChange} options={plantOptions} value={plantValue} required error={error.plantId} />
							</>
						) : null}

						<Dropdown
							id="deviceType"
							name="deviceType"
							label="Device Type"
							onChange={onDropdownChange}
							options={deviceTypeOptions}
							value={deviceTypeValue}
							error={error.deviceType}
							required
						/>
						{formData.deviceType == "rfidReader" || formData.deviceType == "led" ? (
							<Dropdown
								id="deviceSubType"
								name="deviceSubType"
								label="Device Sub Type"
								onChange={onDropdownChange}
								options={formData.deviceType == "led" ? ledSubTypeOptions : rfidSubTypeOptions}
								value={deviceSubTypeValue}
								required
								error={error.deviceSubType}
							/>
						) : null}
						{isHardwareTypeDropdown ? (
							<Dropdown id="hardwareType" name="hardwareType" label="Hardware Type" onChange={onDropdownChange} options={hardwareTypeOpts} value={hardwareTypeValue} />
						) : (
							<Input id="hardwareType" name="hardwareType" label="Hardware Type" placeholder="Hardware Type" value={formData.hardwareType} onChange={handleFormChange} />
						)}
						<Input id="serialNo" name="serialNo" label="Serial No" placeholder="Serial No" value={formData.serialNo} onChange={handleFormChange} />

						<Input label="Display Name" placeholder="Enter Display Name" name="displayName" value={formData.displayName} onChange={handleFormChange} error={error.displayName} required />
						<Input
							label="IMEI / Mac Address"
							placeholder="Enter IMEI"
							name="imei"
							value={formData.imei}
							onChange={handleFormChange}
							required
							error={error.imei}
							// readOnly={updateData?.imei && isUpdate}
						/>

						{formData.deviceType == "rfidReader" && (
							<Dropdown
								id="dataTarget"
								name="dataTarget"
								label="Data Target"
								onChange={onDropdownChange}
								options={dataTargetOption}
								value={dataTargetValue}
								error={error.dataTarget}
								disableClearable={true}
							/>
						)}

						<Dropdown
							id="characterEncoding"
							name="characterEncoding"
							label="Character Encoding"
							onChange={onDropdownChange}
							options={charEncodingOptions}
							value={charEncodingValue}
							error={error.charEncodingValue}
						/>
						{formData.deviceType == "rfidReader" ? (
							<>
								<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
									<Input
										id="deduplicationTimeout"
										type="number"
										name="deduplicationTimeout"
										label="Deduplication Timeout"
										onChange={handleFormChange}
										value={formData.deduplicationTimeout}
									/>
									<Tooltip title="Delay(in sec) to prevent repeated scans">
										<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									</Tooltip>
								</div>
								<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
									<Input id="outOfRangeTimeout" type="number" name="outOfRangeTimeout" label="Out Of Range Timeout" onChange={handleFormChange} value={formData.outOfRangeTimeout} />
									<Tooltip title="Delay(in sec) before marking tag as out of range">
										<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									</Tooltip>
								</div>
							</>
						) : null}
						{showSpeakerInfo ? (
							<>
								<Dropdown
									id="speechLanguage"
									name="speechLanguage"
									label="Speech Language"
									onChange={onDropdownChange}
									options={speechLanguageOptions}
									value={speechLanguageValue}
									error={error.speechLanguage}
								/>

								{formData.speechLanguage ? (
									<Dropdown
										id="speechVoice"
										name="speechVoice"
										label="Speech Voice"
										onChange={onDropdownChange}
										options={speechVoiceOptions}
										value={speechVoiceValue}
										error={error.speechVoice}
									/>
								) : null}
							</>
						) : null}
						{showLedInfo ? (
							<>
								<Dropdown
									id="ledMsgType"
									name="ledMsgType"
									label="LED Message Type"
									onChange={onDropdownChange}
									options={ledStatusOpts}
									value={ledMsgTypeValue}
									error={error.ledMsgType}
									required
								/>
								{formData.ledMsgType == "combo" ? (
									<Input
										label="LED Static Message"
										placeholder="Enter static message"
										name="ledStaticMsg"
										value={formData.ledStaticMsg}
										onChange={handleFormChange}
										error={error.ledStaticMsg}
									/>
								) : null}
							</>
						) : null}
					</div>
					<div>
						{formData.deviceType == "rfidReader" ? (
							<>
								<Checkbox id="proximityMode" name="proximityMode" checked={formData.proximityMode} onChange={handleFormChange} label="Proximity Mode" />
								<Tooltip title="Enable this to allow Out-Of-Range events to be detected through this device.">
									<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
								</Tooltip>
							</>
						) : null}
						{formData.deviceType == "rfidReader" || formData.deviceType == "camera" ? (
							<>
								<Checkbox
									id="disableVahanVerification"
									name="disableVahanVerification"
									checked={formData.disableVahanVerification}
									onChange={handleFormChange}
									label="Disable Vahan Verification"
								/>
							</>
						) : null}
					</div>
					{isDefaultConfigFetching ? (
						<Loader size="3rem" height="50vh" />
					) : showFields ? (
						<>
							{showSeparators ? (
								<Accordion sx={{
									backgroundColor: "var(--set-background-color)",
									color: "var(--set-text-color)",
									border: "1px solid var(--set-table-header-bg-color)",
								}}>
									<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
										backgroundColor: "var(--set-navbar-bg-color)",
										color: "var(--set-heading-text-color)",
										"& .MuiAccordionSummary-content": {
										margin: "8px 0",
										},
									}}>
										<h5>Separators</h5>
									</AccordionSummary>
									<AccordionDetails>
										<div className="form-inputs">
											<Input
												label="Data Separator"
												placeholder="Enter Data Separator"
												name="dataSeparator"
												value={formData.dataSeparator}
												onChange={handleFormChange}
												error={error.dataSeparator}
											/>
											<Input
												label="Start Separator"
												placeholder="Enter Start Separator"
												name="startSeparator"
												value={formData.startSeparator}
												onChange={handleFormChange}
												error={error.startSeparator}
											/>
											<Input
												label="End Separator"
												placeholder="Enter End Separator"
												name="endSeparator"
												value={formData.endSeparator}
												onChange={handleFormChange}
												error={error.endSeparator}
											/>
										</div>
									</AccordionDetails>
								</Accordion>
							) : null}
							{showInfo ? (
								<Accordion sx={{
									backgroundColor: "var(--set-background-color)",
									color: "var(--set-text-color)",
									border: "1px solid var(--set-table-header-bg-color)",
								}}>
									<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
										backgroundColor: "var(--set-navbar-bg-color)",
										color: "var(--set-heading-text-color)",
										"& .MuiAccordionSummary-content": {
										margin: "8px 0",
										},
									}}>
										<h5>Config</h5>
									</AccordionSummary>
									<AccordionDetails>
										<div className="form-inputs">
											{showCIndex ? (
												<Input
													label="Command Index"
													type="number"
													placeholder="Enter Command Index"
													name="commandIndex"
													value={formData.commandIndex}
													onChange={handleFormChange}
													error={error.commandIndex}
												/>
											) : null}
											{showTid ? (
												<Input
													label="TID Pattern"
													placeholder="Enter TID Pattern"
													name="tidPattern"
													value={formData.tidPattern}
													onChange={handleFormChange}
													error={error.tidPattern}
												/>
											) : null}
											{showPower ? <Input label="Power" placeholder="Enter Power" name="power" value={formData.power} onChange={handleFormChange} /> : null}
										</div>
									</AccordionDetails>
								</Accordion>
							) : null}
							{showLogs ? (
								<Accordion sx={{
									backgroundColor: "var(--set-background-color)",
									color: "var(--set-text-color)",
									border: "1px solid var(--set-table-header-bg-color)",
								}}>
									<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
										backgroundColor: "var(--set-navbar-bg-color)",
										color: "var(--set-heading-text-color)",
										"& .MuiAccordionSummary-content": {
										margin: "8px 0",
										},
									}}>
										<h5>Log Patterns</h5>
									</AccordionSummary>
									<AccordionDetails>
										{logs?.map((log, li) => {
											return (
												<div key={li} className="border flex-col-g1  log_pat_padding boder-rad-10p">
													<div className="d-flex-g1 log_patrn_ar">
														<Button onClick={addLog} text="Add" style={buttonClasses.lynkitOrangeEmpty} />
														{li > 0 ? (
															<Button
																className="failBtn"
																variant="outline-danger"
																size="sm"
																onClick={() => removeLog(li)}
																text="Remove"
																style={buttonClasses.lynkitOrangeEmpty}
															/>
														) : null}
													</div>
													<Input label="Command Code" placeholder="Command Code" name="commandCode" value={log.commandCode} onChange={(e) => handleLogChange(e, li)} />

													<div style={{ display: "grid", gridTemplateColumns: "20% 70%", gap: "1rem" }}>
														<Checkbox id="isLog" name="isLog" checked={log.isLog} onChange={(e) => handleLogChange(e, li)} label="Log Time" />
														<Input
															label="Log Time Index"
															placeholder="Enter Log Time Index"
															name="logTime"
															type="number"
															disabled={!log.isLog}
															value={log.logTime}
															onChange={(e) => handleLogChange(e, li)}
														/>
													</div>

													<div className="">
														{log.keys?.map(({ key, value }, i) => {
															return (
																<div key={i} className="form-inputs" style={{ marginBottom: "0.5rem" }}>
																	<Input label="Key" placeholder="Enter Key" name="key" value={key} onChange={(e) => handleKeysChange(e, li, i)} />
																	<Input label="Index" placeholder="Enter Index" name="value" value={value} onChange={(e) => handleKeysChange(e, li, i)} />
																	<AddRemove list={log.keys} filterMethod={(k) => k.key == ""} index={li} outerIndex={i} onAdd={addKey} onRemove={removeKey} />
																</div>
															);
														})}
													</div>
												</div>
											);
										})}
									</AccordionDetails>
								</Accordion>
							) : null}
							{showVC ? (
								<Accordion sx={{
									backgroundColor: "var(--set-background-color)",
									color: "var(--set-text-color)",
									border: "1px solid var(--set-table-header-bg-color)",
								}}>
									<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
										backgroundColor: "var(--set-navbar-bg-color)",
										color: "var(--set-heading-text-color)",
										"& .MuiAccordionSummary-content": {
										margin: "8px 0",
										},
									}}>
										<h5>Value Configs</h5>
									</AccordionSummary>
									<AccordionDetails>
										{configs.map(({ key, value }, ci) => {
											return (
												<div key={ci} className="form-inputs mb-2">
													<Input label="Key" placeholder="Enter Key" name="key" value={key} onChange={(e) => handleConfigChange(e, ci)} />
													<Input label="Index" placeholder="Enter Index" name="value" value={value} onChange={(e) => handleConfigChange(e, ci)} />
													<AddRemove list={configs} filterMethod={(k) => k.key == ""} index={ci} outerIndex={ci} onAdd={addConfig} onRemove={removeConfig} />
												</div>
											);
										})}
									</AccordionDetails>
								</Accordion>
							) : null}

							{showAnt ? (
								<Accordion sx={{
									backgroundColor: "var(--set-background-color)",
									color: "var(--set-text-color)",
									border: "1px solid var(--set-table-header-bg-color)",
								}}>
									<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
										backgroundColor: "var(--set-navbar-bg-color)",
										color: "var(--set-heading-text-color)",
										"& .MuiAccordionSummary-content": {
										margin: "8px 0",
										},
									}}>
										<h5>Antennas</h5>
									</AccordionSummary>
									<AccordionDetails>
										{antenna.map(({ antennanumber, status, action, checkpointId, readType }, aIndex) => {

											return (
												<div key={aIndex} className="form-inputs mb-3">
													<Dropdown
														name="readType"
														label="Read Type"
														onChange={(option, name) => handleAntennaChange({ target: { name, value: option?.value || "" } }, aIndex)}
														options={readTypeOpts}
														value={readTypeOpts.find((_) => _.value == readType)}
													/>

													<Dropdown
														name="checkpointId"
														label="Checkpoint"
														onChange={(option, name) => handleAntennaChange({ target: { name, value: option?.value || "" } }, aIndex)}
														options={checkpointOptions}
														value={checkpointOptions.find((_) => _.value == checkpointId)}
													/>
													<Input
														label="Antenna Number"
														placeholder="Enter Antenna Number"
														name="antennanumber"
														value={antennanumber}
														onChange={(e) => handleAntennaChange(e, aIndex)}
													/>
													<Dropdown
														id="status"
														name="status"
														label="Status"
														onChange={(option, name) => handleAntennaChange({ target: { name, value: option?.value || "" } }, aIndex)}
														options={statusOptions}
														value={statusOptions.find((_) => _.value == status)}
													/>

													<Input label="Action" placeholder="Enter Action" name="action" value={action} onChange={(e) => handleAntennaChange(e, aIndex)} />
													<AddRemove
														list={antenna}
														filterMethod={(k) => k.antennanumber == ""}
														index={aIndex}
														outerIndex={aIndex}
														onAdd={addAntenna}
														onRemove={removeAntenna}
													/>
												</div>
											);
										})}
									</AccordionDetails>
								</Accordion>
							) : null}
						</>
					) : null}

					{formData.deviceType == "rfidReader" || formData.deviceType == "led" || formData.deviceType == "speaker" ? (
						<Accordion sx={{
							backgroundColor: "var(--set-background-color)",
							color: "var(--set-text-color)",
							border: "1px solid var(--set-table-header-bg-color)",
						}}>
							<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
								backgroundColor: "var(--set-navbar-bg-color)",
								color: "var(--set-heading-text-color)",
								"& .MuiAccordionSummary-content": {
								margin: "8px 0",
								},
  							}}>
								<h5>Init Configuration</h5>
							</AccordionSummary>
							<AccordionDetails>
								<DndList
									items={formData.initConfig}
									moveItem={moveInitConfigItem}
									renderItem={(config, index) => (
										<div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
											<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-2 border boder-rad-10p">
												<div style={{ width: "250px" }}>
													<Dropdown
														id={`type-${index}`}
														name="type"
														label="Type"
														onChange={(option) => handleInitConfigDropdownChange(option, "type", index)}
														options={initConfigTypeOptions}
														value={initConfigTypeOptions.find((_) => _.value === config.type)}
														required
													/>
												</div>
												<div style={{ display: "flex" }}>
											{/* AntennaPower type */}
													{config.type === "AntennaPower" ? (
														<div style={{ display: "flex", gap: "1rem" }}>
															<div style={{ minWidth: "200px" }}>
																<Dropdown
																	id={`antennaId-${index}`}
																	name="antennaId"
																	label="Antenna"
																	onChange={(option) => handleInitConfigDropdownChange(option, "antennaId", index)}
																	options={antennaIdOpts}
																	value={antennaIdOpts.find((_) => _.value === config.antennaId) || null}
																	required
																/>
															</div>
															<div style={{minWidth:"180px"}}>
																<Input
																	id={`power-${index}`}
																	name="power"
																	label="Power"
																	type="number"
																	min="0"
																	value={config.power ?? ""}
																	onChange={(e) => handleInitConfigDropdownChange(e.target.value, "power", index)}
																	required
																/>
															</div>
																
														</div>
														) : formData.deviceType === "led" || formData.deviceType === "speaker" ? (
															formData.initConfig[index].type === "static" ? (
																<div style={{ display: "flex", gap: "1rem" }}>
																<Input
																	label="Line One"
																	placeholder="Enter Line One"
																	name="lineOne"
																	value={config.command?.lineOne || ""}
																	onChange={(e) => handleInitConfigChange(e, index)}
																	required
																/>
																<Input
																	label="Line Two"
																	placeholder="Enter Line Two"
																	name="lineTwo"
																	value={config.command?.lineTwo || ""}
																	onChange={(e) => handleInitConfigChange(e, index)}
																	required={formData.deviceSubType === "TWOLINE"}
																/>
															</div>
														) : (
															<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
																<Input
																	label="Generator Function"
																	placeholder="Generator Function"
																	name="command"
																	value={typeof config.command === "string" ? config.command : ""}
																	onChange={(e) => handleInitConfigChange(e, index)}
																	required
																/>

																{config.parameters?.map((param, paramIndex) => (
																	<div key={paramIndex} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
																		{" "}
																		<Input
																			label="Parameters"
																			placeholder="Enter Parameters"
																			name="parameters"
																			value={param}
																			onChange={(e) => handleParameterChange(e, index, paramIndex)}
																		/>
																		<AddRemove
																			list={config.parameters}
																			filterMethod={(param) => param === ""}
																			index={index}
																			outerIndex={paramIndex}
																			onAdd={() => addParameter(index)}
																			onRemove={() => removeParameter(index, paramIndex)}
																		/>
																	</div>
																))}
															</div>
														)
													) : formData.initConfig[index].type === "static" ? (
														<div style={{ minWidth: "225px" }}>
															<Dropdown
																id={`command-${index}`}
																name="command"
																label="Command"
																onChange={(option) => handleInitConfigDropdownChange(option, "command", index)}
																options={gpoOptions}
																value={gpoOptions.find((_) => _.value === config.command) || null}
																required
															/>
														</div>
													) : (
														<div style={{ display: "flex", gap: "1rem" }}>
															<Input
																label="Generator Function"
																placeholder="Generator Function"
																name="command"
																value={typeof config.command === "string" ? config.command : ""}
																onChange={(e) => handleInitConfigChange(e, index)}
																required
															/>
															{config.parameters?.map((param, paramIndex) => (
																<div key={paramIndex} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
																	{" "}
																	<Input
																		label="Parameters"
																		placeholder="Enter Parameters"
																		name="parameters"
																		value={param}
																		onChange={(e) => handleParameterChange(e, index, paramIndex)}
																	/>
																	<AddRemove
																		list={config.parameters}
																		filterMethod={(param) => param === ""}
																		index={index}
																		outerIndex={paramIndex}
																		onAdd={() => addParameter(index)}
																		onRemove={() => removeParameter(index, paramIndex)}
																	/>
																</div>
															))}
														</div>
													)}
												</div>
												<Checkbox
													id={`isActive-${index}`}
													name="isActive"
													checked={config.isActive || false}
													label="Is Active?"
													onChange={(e) => handleInitConfigChange(e, index)}
												/>
												<Input
													type="number"
													id={`duration-${index}`}
													name="duration"
													value={config.duration / 1000}
													label="Next command delay (sec)"
													onChange={(e) => handleInitConfigChange(e, index)}
												/>
											</div>
											<AddRemove
												list={formData.initConfig}
												filterMethod={(item) => {
													if (!item.type) return true;
													if (item.type === "AntennaPower") {
														return !item.antennaId || item.power === "" || item.power === undefined;
													}
													return !item.command;
												}}
												onAdd={addInitConfig}
												onRemove={removeInitConfig}
												index={index}
												outerIndex={index}
											/>
										</div>
									)}
								/>
							</AccordionDetails>
						</Accordion>
					) : (
						<></>
					)}

					{(formData.deviceType == "rfidReader" && formData.deviceSubType == "customReader") ? <Accordion sx={{
							backgroundColor: "var(--set-background-color)",
							color: "var(--set-text-color)",
							border: "1px solid var(--set-table-header-bg-color)",
						}}>
							<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
								backgroundColor: "var(--set-navbar-bg-color)",
								color: "var(--set-heading-text-color)",
								"& .MuiAccordionSummary-content": {
								margin: "8px 0",
								},
  							}}>
								<h5>Alert Configuration</h5>
							</AccordionSummary>
							<AccordionDetails>
								<h6>JbOpen</h6>
								<div style={{ display: "flex", gap: "0.5rem", width: "100%", marginBottom : '10px' }}>
									<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-3 border boder-rad-10p ">
										<Input
											type="number"
											id={`Alert Threshold`}
											name="alertThreshold"
											value={formData.alertConfig.JbOpen.alertThreshold || 0}
											label="Alert Threshold (In sec)"
											onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, 'JbOpen')}
											required
											error={error.JbOpenAlertThreshold}
											min="0"
										/>
										<Input
											type="number"
											id={`cooldownPeriod`}
											name="cooldownPeriod"
											value={formData.alertConfig.JbOpen.cooldownPeriod || 0}
											label="Cooldown Period (In sec)"
											onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, 'JbOpen')}
											required
											error={error.JbOpenCooldownPeriod}
											min="0"
										/>
										<div style={{ display : "flex", alignItems :"center", minWidth: "280px" }}>
											<Dropdown
												id={`notifyEmails`}
												name="notifyEmails"
												label="Notify Emails"
												onChange={(option, name) => handleAlertConfigChange(option, name, 'JbOpen')}
												options={formData.alertConfig.JbOpen.notifyEmails?.length > 0 ? formData.alertConfig.JbOpen.notifyEmails.map((el) => ({'label' : el, 'value' : el})) : []}
												value={formData.alertConfig.JbOpen.notifyEmails?.length > 0 ? formData.alertConfig.JbOpen.notifyEmails.map((el) => ({'label' : el, 'value' : el})) : []}
												enableCustomValue={true}
												validateValue={(value)=> valid_email(value)}
												error={error.JbOpenNotifyEmails}
												multiple={true}
												required
											/>
											<Tooltip style={{marginLeft: '1px'}} title="Type email and press Enter to add to list. Multiple emails supported.">
												<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									        </Tooltip>
										</div>
										
										<div style={{ display : "flex", alignItems :"center", minWidth: "280px" }}>
											<Dropdown
												id={`notifyMobiles`}
												name="notifyMobiles"
												label="Notify Mobiles"
												onChange={(option, name) => handleAlertConfigChange(option, name, 'JbOpen')}
												options={formData.alertConfig.JbOpen.notifyMobiles?.length > 0 ? formData.alertConfig.JbOpen.notifyMobiles.map((el) => ({'label' : el, 'value' : el})) :  []}
												value={formData.alertConfig.JbOpen.notifyMobiles?.length > 0 ? formData.alertConfig.JbOpen.notifyMobiles.map((el) => ({'label' : el, 'value' : el})) : []}
												enableCustomValue={true}
												validateValue={(value)=> valid_mobile(value)}
												error={error.JbOpenNotifyMobiles}
												multiple={true}
												required
											/>
											<Tooltip style={{marginLeft: '1px'}} title="Type mobile and press Enter to add to list. Multiple mobile number supported.">
												<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									        </Tooltip>
										</div>
												
									</div>	
								</div>
								<h6>BoomOpen</h6>
								<div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
									<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-3 border boder-rad-10p ">
										<Input
											type="number"
											id={`Alert Threshold`}
											name="alertThreshold"
											value={formData.alertConfig.BoomOpen.alertThreshold || 0}
											label="Alert Threshold (In sec)"
											onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, 'BoomOpen')}
											required={true}
											error={error.BoomOpenAlertThreshold}
											min="0"
										/>
										<Input
											type="number"
											id={`cooldownPeriod`}
											name="cooldownPeriod"
											value={formData.alertConfig.BoomOpen.cooldownPeriod || 0}
											label="Cooldown Period (In sec)"
											onChange={(e) => handleAlertConfigChange(e.target.value, e.target.name, 'BoomOpen')}
											required={true}
											error={error.BoomOpenCooldownPeriod}
											min="0"
										/>
										<div style={{ display : "flex", alignItems :"center", minWidth: "280px" }}>
											<Dropdown
												id={`notifyEmails`}
												name="notifyEmails"
												label="Notify Emails"
												onChange={(option, name) => handleAlertConfigChange(option, name, 'BoomOpen')}
												options={formData.alertConfig.BoomOpen.notifyEmails?.length > 0 ? formData.alertConfig.BoomOpen.notifyEmails.map((el) => ({'label' : el, 'value' : el})) : []}
												value={formData.alertConfig.BoomOpen.notifyEmails?.length > 0 ? formData.alertConfig.BoomOpen.notifyEmails.map((el) => ({'label' : el, 'value' : el})) : []}
												enableCustomValue={true}
												validateValue={(value)=> valid_email(value)}
												error={error.BoomOpenNotifyEmails}
												multiple={true}
												required
											/>
											<Tooltip style={{marginLeft: '1px'}} title="Type email and press Enter to add to list. Multiple emails supported.">
												<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									        </Tooltip>
										</div>
										<div style={{ display : "flex", alignItems :"center", minWidth: "280px" }}>
											<Dropdown
												id={`notifyMobiles`}
												name="notifyMobiles"
												label="Notify Mobiles"
												onChange={(option, name) => handleAlertConfigChange(option, name, 'BoomOpen')}
												options={formData.alertConfig.BoomOpen.notifyMobiles?.length > 0 ? formData.alertConfig.BoomOpen.notifyMobiles.map((el) => ({'label' : el, 'value' : el})) :  []}
												value={formData.alertConfig.BoomOpen.notifyMobiles?.length > 0 ? formData.alertConfig.BoomOpen.notifyMobiles.map((el) => ({'label' : el, 'value' : el})) : []}
												enableCustomValue={true}
												validateValue={(value)=> valid_mobile(value)}
												error={error.BoomOpenNotifyMobiles}
												multiple={true}
												required
											/>
											<Tooltip style={{marginLeft: '1px'}} title="Type mobile and press Enter to add to list. Multiple mobile number supported.">
												<InfoIcon sx={{ fill: "#ff7200", marginLeft: "-15px", marginRight: "15px" }} />
									        </Tooltip>
										</div>
												
									</div>	
								</div>
							</AccordionDetails>
						</Accordion>: null}
					
					{formData?.deviceType === "workstation" 
						?
						<Accordion sx={{
							backgroundColor: "var(--set-background-color)",
							color: "var(--set-text-color)",
							border: "1px solid var(--set-table-header-bg-color)",
						}}>
							<AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--set-icon-color)" }}/>} sx={{
								backgroundColor: "var(--set-navbar-bg-color)",
								color: "var(--set-heading-text-color)",
								"& .MuiAccordionSummary-content": {
								margin: "8px 0",
								},
  							}}>
								<h5>Offline Configuration</h5>
							</AccordionSummary>
							<AccordionDetails sx={{
								backgroundColor: "var(--set-background-color)",
								color: "var(--set-text-color)",
							}}>
								<h6>Gate In Service</h6>
									<div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
										<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-3 border boder-rad-10p ">
											<Input
												type="number"
												id={`boomTimeout`}
												name="boomTimeout"
												value={formData.offlineConfiguration.gateInService.boomTimeout || 0}
												label="Boom Timeout"
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateInService')}
												// required={true}
												min="0"
												
											/>
											<Input
												type="input"
												id={`ledPrefix`}
												name="ledPrefix"
												value={formData.offlineConfiguration.gateInService.ledPrefix || ""}
												label="LED Prefix"
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateInService')}
												// required={true}
												
											/>
											<Input
												type="input"
												id={`ledSuffix`}
												name="ledSuffix"
												value={formData.offlineConfiguration.gateInService.ledSuffix || ""}
												label="LED Suffix"
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateInService')}
												// required={true}
												
											/>		
										</div>	
									</div>
								<h6 style={{marginTop:"0.5rem"}}>Gate Out Service</h6>
									<div style={{ display: "flex", gap: "0.5rem", width: "100%" }}>
										<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }} className="p-3 border boder-rad-10p ">
											<Input
												type="number"
												id={`boomTimeout`}
												name="boomTimeout"
												label="Boom Timeout"
												value={formData.offlineConfiguration.gateOutService.boomTimeout || 0}
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateOutService')}
												min="0"
											/>
											<Input
												type="input"
												id={`ledPrefix`}
												name="ledPrefix"
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateOutService')}
												label="LED Prefix"
												value={formData.offlineConfiguration.gateOutService.ledPrefix || ""}
												// required={true}
												
											/>
											<Input
												type="input"
												id={`ledSuffix`}
												name="ledSuffix"
												value={formData.offlineConfiguration.gateOutService.ledSuffix || ""}
												label="LED Suffix"
												onChange={(e) => handleOfflineConfigChange(e.target.value, e.target.name, 'gateOutService')}
												// required={true}
												
											/>		
										</div>	
									</div>
							</AccordionDetails>
						</Accordion>
						: null
					}

					<div className="d-flex justify-content-center" style={{ height: "79px" }}>
						<Button onClick={handleFormValidation} text={isUpdate ? "Update" : "Submit"} style={{ ...buttonClasses.lynkitOrangeFill, width: "15%" }} disabled={isAdding || isUpdating} />
					</div>
				</form>
			</div>
		</>
	);
};
export default AddDeviceForm;

const statusOptions = [
	{ label: "--select--", value: "" },
	{ label: "Active", value: "Active" },
	{ label: "In-Active", value: "In-Active" },
];

const deviceTypeOptions = [
	{ label: "RFID Reader", value: "rfidReader" },
	{ label: "Speaker", value: "speaker" },
	{ label: "LED", value: "led" },
	{ label: "Camera", value: "camera" },
	{ label: "Workstation", value: "workstation" },
];

const rfidSubTypeOptions = [
	{ label: "Zebra", value: "zebra" },
	{ label: "Seuic", value: "seuic" },
	{ label: "Custom Reader", value: "customReader" },
];
const hardwareTypeOptions = [
	{ label: "Zebra", value: "zebra" },
	{ label: "Seuic", value: "seuic" },
	{ label: "Royal Ray", value: "royalRay" },
];
const ledSubTypeOptions = [
	{ label: "Two Line", value: "TWOLINE" },
	{ label: "Ten Line", value: "TENLINE" },
];
const charEncodingOptions = [
	{ label: "UTF-8", value: "utf-8" },
	{ label: "Hex (combine w/o separators)", value: "hex" },
];
const dataTargetOption = [
	{ label: "LYNKGRID", value: "LYNKGRID" },
	{ label: "VMS", value: "VMS" },
];

const ledStatusOpts = [
	{ label: "Display on top-half", value: "top" },
	{ label: "Display on bottom-half", value: "bottom" },
	{ label: "Display full screen scrolling", value: "scroll" },
	{ label: "Static on line 1, Scrolling on line 2", value: "combo" },
];

const ItemTypes = {
	CONFIG_ITEM: "configItem",
};

const readTypeOpts = [{label: "Eseal", value: "Eseal"}, {label: "Fastag", value: "Fastag"}]
const DraggableItem = ({ id, index, moveItem, children }) => {
	const ref = React.useRef(null);

	const [{ isDragging }, drag, preview] = useDrag({
		type: ItemTypes.CONFIG_ITEM,
		item: { id, index },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [, drop] = useDrop({
		accept: ItemTypes.CONFIG_ITEM,
		hover: (item, monitor) => {
			if (!ref.current) return;
			const dragIndex = item.index;
			const hoverIndex = index;

			if (dragIndex === hoverIndex) return;

			const hoverBoundingRect = ref.current.getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

			moveItem(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	drag(drop(ref));

	return (
		<div
			ref={preview}
			style={{
				opacity: isDragging ? 0.5 : 1,
				display: "flex",
				alignItems: "center",
				width: "100%",
				marginBottom: "20px",
			}}
		>
			<div ref={ref}>
				<DragIndicatorIcon />
			</div>
			{children}
		</div>
	);
};

const DndList = ({ items, moveItem, renderItem }) => {
	return (
		<DndProvider backend={HTML5Backend}>
			<div style={{ width: "100%" }}>
				{items.map((item, index) => (
					<DraggableItem key={item.id || index} index={index} id={item.id || index} moveItem={moveItem}>
						{renderItem(item, index)}
					</DraggableItem>
				))}
			</div>
		</DndProvider>
	);
};

const cameraHardwareTypeOpt = [{ label: "IN-IPC2N25P-15U (M)SD", value: "IN-IPC2N25P-15U (M)SD" }];
